import React, { useState, useEffect } from 'react';
import { Search, Plus, X, CheckCircle, AlertCircle, Users, Package, CreditCard } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import clientPaymentService from '../services/clientPaymentService';
import stripeSyncService from '../services/stripeSyncService';

/**
 * Composant d'attribution des produits Stripe aux clients
 * Interface d'administration pour assigner des produits de facturation aux clients
 */
const ClientProductAssignment = ({ db, currentUser }) => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [clientAssignments, setClientAssignments] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterData();
  }, [searchTerm, clients, products]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les clients et produits en parallèle
      const [clientsData, productsData] = await Promise.all([
        loadClients(),
        loadProducts()
      ]);

      setClients(clientsData);
      setProducts(productsData);

      // Charger les attributions existantes
      await loadClientAssignments(clientsData);

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement des données'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      return clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      return [];
    }
  };

  const loadProducts = async () => {
    try {
      // Récupérer uniquement les produits de facturation
      const billingProducts = await stripeSyncService.getBillingProducts();
      return billingProducts.filter(product => product.active);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      return [];
    }
  };

  const loadClientAssignments = async (clientsList) => {
    try {
      const assignments = {};
      
      for (const client of clientsList) {
        const clientProducts = await clientPaymentService.getClientProducts(client.id);
        assignments[client.id] = clientProducts.map(p => p.productId);
      }
      
      setClientAssignments(assignments);
    } catch (error) {
      console.error('Erreur lors du chargement des attributions:', error);
    }
  };

  const filterData = () => {
    if (!searchTerm.trim()) {
      setFilteredClients(clients);
      setFilteredProducts(products);
      return;
    }

    const term = searchTerm.toLowerCase();
    
    const filteredClients = clients.filter(client => 
      client.firstName?.toLowerCase().includes(term) ||
      client.lastName?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term)
    );

    const filteredProducts = products.filter(product =>
      product.name?.toLowerCase().includes(term) ||
      product.description?.toLowerCase().includes(term)
    );

    setFilteredClients(filteredClients);
    setFilteredProducts(filteredProducts);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSelectedProducts([]);
  };

  const handleProductToggle = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const isProductAssigned = (clientId, productId) => {
    return clientAssignments[clientId]?.includes(productId) || false;
  };

  const assignProducts = async () => {
    if (!selectedClient || selectedProducts.length === 0) {
      setMessage({
        type: 'warning',
        text: 'Veuillez sélectionner un client et au moins un produit'
      });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const results = [];
      
      for (const product of selectedProducts) {
        try {
          const result = await clientPaymentService.assignProductToClient(
            selectedClient.id,
            product.id,
            {
              assignedBy: currentUser?.uid || 'admin',
              notes: `Attribué via interface admin`
            }
          );
          results.push({ success: true, product: product.name, result });
        } catch (error) {
          results.push({ success: false, product: product.name, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;

      if (errorCount === 0) {
        setMessage({
          type: 'success',
          text: `${successCount} produit(s) attribué(s) avec succès à ${selectedClient.firstName} ${selectedClient.lastName}`
        });
      } else {
        setMessage({
          type: 'warning',
          text: `${successCount} produit(s) attribué(s), ${errorCount} erreur(s)`
        });
      }

      // Recharger les données
      await loadClientAssignments(clients);
      setSelectedProducts([]);

    } catch (error) {
      console.error('Erreur lors de l\'attribution des produits:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors de l\'attribution des produits'
      });
    } finally {
      setLoading(false);
    }
  };

  const removeProductAssignment = async (clientId, productId) => {
    try {
      setLoading(true);
      
      await clientPaymentService.removeProductFromClient(clientId, productId);
      
      // Mettre à jour les attributions locales
      setClientAssignments(prev => ({
        ...prev,
        [clientId]: prev[clientId]?.filter(id => id !== productId) || []
      }));

      setMessage({
        type: 'success',
        text: 'Produit retiré avec succès'
      });

    } catch (error) {
      console.error('Erreur lors du retrait du produit:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du retrait du produit'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return (price / 100).toFixed(2) + ' €';
  };

  const getProductPrice = (product) => {
    const mainPrice = product.prices.find(price => price.active) || product.prices[0];
    return mainPrice?.unit_amount || 0;
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Attribution des produits
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Attribuez des produits de facturation Stripe aux clients
              </p>
            </div>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Search className="w-4 h-4 mr-2" />
            Actualiser
          </button>
        </div>

        {/* Barre de recherche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher un client ou un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Liste des clients */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-500" />
            Clients ({filteredClients.length})
          </h3>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredClients.map(client => (
              <div
                key={client.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedClient?.id === client.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => handleClientSelect(client)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {client.firstName} {client.lastName}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {client.email}
                    </p>
                    {clientAssignments[client.id]?.length > 0 && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        {clientAssignments[client.id].length} produit(s) attribué(s)
                      </p>
                    )}
                  </div>
                  
                  {selectedClient?.id === client.id && (
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                  )}
                </div>
              </div>
            ))}
            
            {filteredClients.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun client trouvé</p>
              </div>
            )}
          </div>
        </div>

        {/* Liste des produits */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Package className="w-5 h-5 mr-2 text-green-500" />
            Produits de facturation ({filteredProducts.length})
          </h3>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredProducts.map(product => {
              const isSelected = selectedProducts.some(p => p.id === product.id);
              const isAssigned = selectedClient && isProductAssigned(selectedClient.id, product.id);
              const price = getProductPrice(product);

              return (
                <div
                  key={product.id}
                  className={`p-4 border rounded-lg transition-colors ${
                    isSelected
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : isAssigned
                      ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/10'
                      : 'border-gray-200 dark:border-gray-600'
                  } ${!isAssigned ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : ''}`}
                  onClick={() => !isAssigned && handleProductToggle(product)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {product.name}
                        </h4>
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {formatPrice(price)}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {product.description}
                      </p>
                      
                      <div className="flex items-center space-x-2 text-xs">
                        {product.metadata?.category && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded">
                            {product.metadata.category}
                          </span>
                        )}
                        {product.prices?.some(p => p.type === 'recurring') && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 rounded">
                            Abonnement
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      {isAssigned ? (
                        <div className="flex items-center text-blue-600 dark:text-blue-400">
                          <CheckCircle className="w-5 h-5 mr-1" />
                          <span className="text-sm">Attribué</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeProductAssignment(selectedClient.id, product.id);
                            }}
                            className="ml-2 p-1 text-red-500 hover:text-red-700"
                            title="Retirer le produit"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : isSelected ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredProducts.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun produit trouvé</p>
              </div>
            )}
          </div>

          {/* Bouton d'attribution */}
          {selectedClient && selectedProducts.length > 0 && (
            <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">
                    Attribuer {selectedProducts.length} produit(s) à {selectedClient.firstName} {selectedClient.lastName}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Total: {formatPrice(selectedProducts.reduce((sum, p) => sum + getProductPrice(p), 0))}
                  </p>
                </div>
                <button
                  onClick={assignProducts}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {loading ? 'Attribution...' : 'Attribuer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 dark:bg-green-900 border-l-4 border-green-500 text-green-700 dark:text-green-300'
            : message.type === 'warning'
            ? 'bg-yellow-100 dark:bg-yellow-900 border-l-4 border-yellow-500 text-yellow-700 dark:text-yellow-300'
            : 'bg-red-100 dark:bg-red-900 border-l-4 border-red-500 text-red-700 dark:text-red-300'
        }`}>
          <div className="flex items-start">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            ) : message.type === 'warning' ? (
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Indicateur de chargement */}
      {loading && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            <span>Chargement...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientProductAssignment;
